import { createContext, useContext, useState, useCallback } from 'react';

const STEP_WELCOME = {
    id: 'welcome',
    title: 'Bienvenue sur PulseCIO !',
    description: 'Faites un tour rapide des fonctionnalités principales. Ce tutoriel vous guidera en quelques étapes. Vous pouvez le passer à tout moment.',
    target: null,
    requiredAction: null,
    blockUI: true,
};

const STEP_CREATE_MESSAGE = {
    id: 'create-message',
    title: 'Créez votre premier message',
    description: 'Les messages sont au cœur de PulseCIO. Créez des modèles de communication réutilisables pour votre équipe. Cliquez sur "Nouveau message" pour commencer.',
    target: 'new-message-btn',
    requiredAction: 'editor-opened',
    blockUI: false,
};

const STEP_DATE_COMMANDS = {
    id: 'date-commands',
    title: 'Dates dynamiques avec //',
    description: 'Tapez // dans la zone de contenu pour insérer des dates dynamiques : //Mois, //Année, //Date+1… Chaque tag se résout automatiquement à l\'affichage. Pratique pour des messages réutilisables chaque mois !',
    target: 'message-content',
    requiredAction: null,
    blockUI: false,
    hint: 'Essayez // dans l\'éditeur ci-dessus',
};

const STEP_MENTIONS_CONTENT = {
    id: 'mentions-content',
    title: 'Mentions @ entre messages et todos',
    description: 'Tapez @ dans la zone de contenu pour mentionner un autre message ou un todo. Cela crée un lien dynamique entre vos contenus, utile pour centraliser l\'information.',
    target: 'message-content',
    requiredAction: null,
    blockUI: false,
    hint: 'Essayez @ dans l\'éditeur ci-dessus',
};

const STEP_MENTIONS_USERS = {
    id: 'mentions-users',
    title: 'Mentions @ d\'utilisateurs',
    description: 'Mentionnez un membre de votre équipe avec @nom directement dans le contenu. Il recevra une notification in-app et pourra retrouver le message depuis ses notifications.',
    target: 'message-content',
    requiredAction: null,
    blockUI: false,
    hint: 'Essayez @prénom dans l\'éditeur ci-dessus',
};

const STEP_SAVE_MESSAGE = {
    id: 'save-message',
    title: 'Sauvegardez votre message',
    description: 'Votre message est prêt ? Donnez-lui un titre et cliquez sur "Créer" pour le sauvegarder. Il sera accessible par toute votre équipe.',
    target: 'message-save-btn',
    requiredAction: 'message-created',
    blockUI: false,
    hint: 'Cliquez sur "Créer" pour valider',
};

const STEP_SEARCH = {
    id: 'search',
    title: 'Recherche rapide (Ctrl+K)',
    description: 'Retrouvez n\'importe quel message instantanément. Cliquez sur la barre de recherche ou appuyez sur Ctrl+K pour ouvrir la palette de recherche.',
    target: 'search-btn',
    requiredAction: 'search-opened',
    // blockUI: false pour éviter les conflits avec la FullTextModal si l'utilisateur sélectionne un message
    blockUI: false,
    hint: 'Cliquez sur la barre ou appuyez sur Ctrl+K',
};

const STEP_TODO = {
    id: 'todo',
    title: 'Panneau TODO',
    description: 'Le panneau TODO vous permet de suivre vos tâches liées à vos messages. Chaque membre de l\'équipe peut consulter et mettre à jour ses todos.',
    target: 'todo-btn',
    requiredAction: 'todo-opened',
    // blockUI: false pour éviter les conflits si une FullTextModal est encore ouverte
    blockUI: false,
    hint: 'Cliquez sur le bouton TODO dans la barre de navigation',
};

const STEP_ORG_SETTINGS = {
    id: 'org-settings',
    title: 'Paramètres de l\'organisation',
    description: 'Gérez votre organisation : membres de l\'équipe, plan d\'abonnement, personnalisation et bien plus. Ouvrez le sélecteur d\'organisation (en haut) puis cliquez sur "Gérer l\'organisation".',
    target: 'org-settings-link',
    fallbackTarget: 'tenant-switcher-btn',
    requiredAction: 'visited-org-settings',
    blockUI: false,
    hint: 'Ouvrez le menu organisation → cliquez sur "Gérer l\'organisation"',
};

const STEP_NOTIFICATIONS = {
    id: 'notifications',
    title: 'Notifications',
    description: 'Retrouvez toutes vos mentions et alertes dans le centre de notifications. Cliquez sur votre profil en haut à droite puis sur "Notifications".',
    target: 'user-menu-btn',
    requiredAction: 'visited-notifications',
    blockUI: false,
    hint: 'Cliquez sur votre nom → Notifications',
};

const STEP_PROFILE = {
    id: 'profile',
    title: 'Votre profil',
    description: 'Personnalisez votre nom, avatar, mot de passe et relancez ce tutoriel à tout moment. Cliquez sur votre profil en haut à droite puis sur "Mon profil".',
    target: 'user-menu-btn',
    requiredAction: 'visited-profile',
    blockUI: false,
    hint: 'Cliquez sur votre nom → Mon profil',
};

const STEP_DONE = {
    id: 'done',
    title: 'Vous êtes prêt !',
    description: 'Bravo, vous connaissez maintenant les fonctionnalités clés de PulseCIO ! N\'hésitez pas à explorer les modèles de messages, les exports et toutes les options disponibles.',
    target: null,
    requiredAction: null,
    blockUI: true,
};

function buildSteps(features = {}) {
    const steps = [STEP_WELCOME, STEP_CREATE_MESSAGE];

    if (features.canUseDateCommands) {
        steps.push(STEP_DATE_COMMANDS);
    }

    if (features.canMentionContent) {
        steps.push(STEP_MENTIONS_CONTENT);
    }

    if (features.canMentionUsers) {
        steps.push(STEP_MENTIONS_USERS);
    }

    steps.push(STEP_SAVE_MESSAGE, STEP_SEARCH, STEP_TODO, STEP_ORG_SETTINGS);

    if (features.showNotifications) {
        steps.push(STEP_NOTIFICATIONS);
    }

    steps.push(STEP_PROFILE, STEP_DONE);
    return steps;
}

const STORAGE_KEY = 'pulsecio_tutorial_done';

const TutorialContext = createContext();

export const TutorialProvider = ({ children }) => {
    const [isActive, setIsActive] = useState(false);
    const [steps, setSteps] = useState([]);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [lastFeatures, setLastFeatures] = useState({});

    const start = useCallback((features = {}) => {
        // Ne pas démarrer si déjà actif ou si déjà complété/passé
        if (isActive || localStorage.getItem(STORAGE_KEY)) return;
        setLastFeatures(features);
        const builtSteps = buildSteps(features);
        setSteps(builtSteps);
        setCurrentStepIndex(0);
        setIsActive(true);
    }, [isActive]);

    const skip = useCallback(() => {
        localStorage.setItem(STORAGE_KEY, 'skipped');
        setIsActive(false);
    }, []);

    const complete = useCallback(() => {
        localStorage.setItem(STORAGE_KEY, 'done');
        setIsActive(false);
    }, []);

    const advanceStep = useCallback(() => {
        setCurrentStepIndex(prev => {
            const next = prev + 1;
            if (next >= steps.length) return prev;
            return next;
        });
    }, [steps.length]);

    const notifyAction = useCallback((actionId) => {
        setCurrentStepIndex(prev => {
            const currentStep = steps[prev];
            if (currentStep && currentStep.requiredAction === actionId) {
                return prev + 1;
            }
            return prev;
        });
    }, [steps]);

    // Relancer le tutoriel (depuis le profil par exemple)
    // Utilise les features passées ou les dernières features connues
    const restart = useCallback((features) => {
        localStorage.removeItem(STORAGE_KEY);
        const f = features !== undefined ? features : lastFeatures;
        setLastFeatures(f);
        const builtSteps = buildSteps(f);
        setSteps(builtSteps);
        setCurrentStepIndex(0);
        setIsActive(true);
    }, [lastFeatures]);

    return (
        <TutorialContext.Provider value={{
            isActive,
            currentStep: steps[currentStepIndex] || null,
            currentStepIndex,
            totalSteps: steps.length,
            steps,
            start,
            skip,
            complete,
            advanceStep,
            notifyAction,
            restart,
        }}>
            {children}
        </TutorialContext.Provider>
    );
};

export const useTutorial = () => useContext(TutorialContext);
